import { createMemory, searchMemories } from '@4cm/brain/brainMemory';
import { callAIJson } from '@4cm/core/ai';
import type { RadarValues } from './queries/useTeamRadar';
import { supabase } from './supabase';
import {
  PLAYER_DEVELOPMENT_ANALYSIS_PROMPT,
  buildPlayerAnalysisUserContent,
} from './prompts/playerDevelopmentAnalysis';
import { DEFAULT_WEIGHT_MATRIX, computeComposite } from './ratingEngine';
import type { WeightMatrix } from './ratingEngine';

type CornerKey = keyof RadarValues;
export type DevelopmentAnalysisCorner = CornerKey | 'general';

export interface PlayerDevelopmentAction {
  corner: DevelopmentAnalysisCorner;
  action: string;
  rationale: string;
}

export interface PlayerDevelopmentAnalysis {
  summary: string;
  actions: PlayerDevelopmentAction[];
  parent_highlight: string;
}

interface RawPlayerDevelopmentAction {
  corner?: unknown;
  action?: unknown;
  rationale?: unknown;
}

interface RawPlayerDevelopmentAnalysis {
  summary?: unknown;
  actions?: unknown;
  parent_highlight?: unknown;
}

interface DraftOptions {
  playerId?: string;
  playerName: string;
  position?: string | null;
  ratings: RadarValues;
  velocity: RadarValues;
  ratingsCount: number;
  goals?: string[];
  planMilestones?: string[];
  clipNotes?: string[];
}

interface SaveOptions {
  coachId: string;
  teamId: string | null;
  playerId: string;
  playerName: string;
  ratings: RadarValues;
  velocity: RadarValues;
  analysis: PlayerDevelopmentAnalysis;
  weights?: WeightMatrix;
  maturityOffset?: number | null;
}

export interface PlayerDevelopmentAnalysisSummary {
  id: string;
  fact: string;
  created_at: string;
}

const CORNER_KEYS: CornerKey[] = ['tech', 'phys', 'psych', 'social'];

const CORNER_LABELS: Record<DevelopmentAnalysisCorner, string> = {
  tech: 'Technical',
  phys: 'Physical',
  psych: 'Psychological',
  social: 'Social',
  general: 'General',
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function normaliseCorner(value: unknown): DevelopmentAnalysisCorner {
  if (typeof value !== 'string') return 'general';
  const clean = value.trim().toLowerCase().replace(/[^a-z]/g, '');
  if (clean === 'tech' || clean === 'technical' || clean === 'technicaltactical') return 'tech';
  if (clean === 'phys' || clean === 'physical') return 'phys';
  if (clean === 'psych' || clean === 'psychological') return 'psych';
  if (clean === 'social') return 'social';
  return 'general';
}

function cleanText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function normaliseAnalysis(payload: RawPlayerDevelopmentAnalysis | null): PlayerDevelopmentAnalysis | null {
  if (!payload || typeof payload !== 'object') return null;
  const summary = cleanText(payload.summary);
  const parentHighlight = cleanText(payload.parent_highlight);
  if (!summary || !parentHighlight || !Array.isArray(payload.actions)) return null;

  const actions = payload.actions
    .map((item): PlayerDevelopmentAction | null => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as RawPlayerDevelopmentAction;
      const action = cleanText(raw.action);
      const rationale = cleanText(raw.rationale);
      if (!action || !rationale) return null;
      return { corner: normaliseCorner(raw.corner), action, rationale };
    })
    .filter((item): item is PlayerDevelopmentAction => item !== null)
    .slice(0, 3);

  if (actions.length !== 3) return null;
  return { summary, actions, parent_highlight: parentHighlight };
}

function strongestCorner(ratings: RadarValues): CornerKey {
  return CORNER_KEYS.reduce((best, key) => (ratings[key] > ratings[best] ? key : best), 'tech');
}

function priorityCorner(ratings: RadarValues, velocity: RadarValues): CornerKey {
  return CORNER_KEYS.reduce((best, key) => {
    const current = ratings[key] - velocity[key] * 0.5;
    const bestValue = ratings[best] - velocity[best] * 0.5;
    return current < bestValue ? key : best;
  }, 'tech');
}

export function fallbackPlayerDevelopmentAnalysis(opts: DraftOptions): PlayerDevelopmentAnalysis {
  const top = strongestCorner(opts.ratings);
  const focus = priorityCorner(opts.ratings, opts.velocity);
  const goal = opts.goals?.find((g) => g.trim())?.trim();
  const milestone = opts.planMilestones?.find((m) => m.trim())?.trim();
  const clip = opts.clipNotes?.find((note) => note.trim())?.trim();

  const summary = opts.ratingsCount > 0
    ? `${opts.playerName} has ${opts.ratingsCount} revealed ratings in the current window. ${CORNER_LABELS[top]} is the strongest signal right now, while ${CORNER_LABELS[focus].toLowerCase()} gives the cleanest next coaching focus.`
    : `${opts.playerName} does not have enough revealed ratings for a trend yet. Start with one observable focus, capture one piece of evidence, and let the next fortnight establish the baseline.`;

  return {
    summary,
    actions: [
      {
        corner: focus,
        action: goal
          ? `Turn the active goal into one touchline cue: ${goal}.`
          : `Pick one ${CORNER_LABELS[focus].toLowerCase()} cue and observe it in the next two sessions.`,
        rationale: 'A single cue makes the next review specific instead of relying on a broad corner score.',
      },
      {
        corner: 'general',
        action: milestone
          ? `Use the current milestone as the next checkpoint: ${milestone}.`
          : 'Set one four-week milestone with a date and a clear evidence moment.',
        rationale: 'A dated checkpoint keeps the development plan visible between formal reviews.',
      },
      {
        corner: clip ? top : 'general',
        action: clip
          ? `Attach the clip note to the next player conversation: ${clip}.`
          : 'Tag one short clip after the next session so the player can see the behaviour.',
        rationale: 'Video evidence makes the feedback concrete and parent-safe without exposing private notes.',
      },
    ],
    parent_highlight: `${opts.playerName} is building a clearer picture of progress through specific goals, evidence, and coach feedback.`,
  };
}

async function recordAnalysisLog(
  playerId: string | undefined,
  analysis: PlayerDevelopmentAnalysis | null,
  success: boolean,
): Promise<void> {
  if (!playerId) return;
  try {
    await supabase.from('ai_analysis_logs').insert({
      entity_type: 'player',
      entity_id: playerId,
      analysis_type: 'player_development_analysis',
      model: 'brain-chat:low',
      prompt_version: 'web2-player-development-analysis-v1',
      response_summary: analysis?.summary ?? null,
      raw_response: analysis ? JSON.stringify(analysis) : null,
      success,
      requires_coach_review: true,
    });
  } catch (err) {
    console.warn('[player analysis] log failed', err);
  }
}

export async function draftPlayerDevelopmentAnalysis(opts: DraftOptions): Promise<PlayerDevelopmentAnalysis | null> {
  try {
    const payload = await callAIJson<RawPlayerDevelopmentAnalysis>({
      system: PLAYER_DEVELOPMENT_ANALYSIS_PROMPT,
      userContent: buildPlayerAnalysisUserContent({
        today: todayIso(),
        playerName: opts.playerName,
        position: opts.position ?? null,
        ratings: opts.ratings,
        velocity: opts.velocity,
        ratingsCount: opts.ratingsCount,
        activeGoals: opts.goals ?? [],
        planMilestones: opts.planMilestones ?? [],
        clipNotes: opts.clipNotes ?? [],
      }),
      maxTokens: 650,
      surface: 'brain-chat',
      effort: 'low',
    });
    const analysis = normaliseAnalysis(payload);
    await recordAnalysisLog(opts.playerId, analysis, analysis !== null);
    return analysis;
  } catch (err) {
    await recordAnalysisLog(opts.playerId, null, false);
    throw err;
  }
}

function formatAnalysisFact(playerName: string, analysis: PlayerDevelopmentAnalysis): string {
  return [
    `Player development analysis for ${playerName} (${new Date().toLocaleDateString()}):`,
    analysis.summary,
    'Actions:',
    ...analysis.actions.map((a, i) => `${i + 1}. [${CORNER_LABELS[a.corner]}] ${a.action} (${a.rationale})`),
    `Parent highlight: ${analysis.parent_highlight}`,
  ].join('\n');
}

async function saveAnalyticsSnapshot(opts: SaveOptions): Promise<string | null> {
  const composite = computeComposite({
    ratings: [
      { corner_id: 'corner-technical', score: opts.ratings.tech },
      { corner_id: 'corner-physical', score: opts.ratings.phys },
      { corner_id: 'corner-psych', score: opts.ratings.psych },
      { corner_id: 'corner-social', score: opts.ratings.social },
    ],
    weights: opts.weights ?? DEFAULT_WEIGHT_MATRIX,
    player: { maturity_offset: opts.maturityOffset ?? null },
  });
  const psychSocialIndex = Math.round((opts.ratings.psych + opts.ratings.social) / 2);
  try {
    const { data, error } = await supabase
      .from('analytics_snapshots')
      .insert({
        team_id: opts.teamId,
        player_id: opts.playerId,
        snapshot_date: todayIso(),
        corner_scores: { ...opts.ratings, composite },
        velocity: opts.velocity,
        psych_social_index: psychSocialIndex,
        transition_readiness: composite,
        response_summary: formatAnalysisFact(opts.playerName, opts.analysis),
      })
      .select('id')
      .single();
    if (error || !data) {
      if (error) console.warn('[player analysis] snapshot save failed', error.message);
      return null;
    }
    return String(data.id);
  } catch (err) {
    console.warn('[player analysis] snapshot save threw', err);
    return null;
  }
}

export async function savePlayerDevelopmentAnalysis(
  opts: SaveOptions,
): Promise<{ ok: true; memoryId: string; snapshotId: string | null } | { ok: false; error: string }> {
  const fact = formatAnalysisFact(opts.playerName, opts.analysis);
  const snapshotId = await saveAnalyticsSnapshot(opts);
  const memory = await createMemory({
    coach_id: opts.coachId,
    team_id: opts.teamId,
    scope: 'player',
    entity_id: opts.playerId,
    kind: 'reflection',
    fact,
    tags: ['player-analysis', 'layer3', 'ai-development'],
    confidence: 0.86,
  });
  if (!memory) return { ok: false, error: 'Could not save player analysis.' };
  return { ok: true, memoryId: memory.id, snapshotId };
}

export async function listPlayerDevelopmentAnalyses(
  coachId: string,
  playerId: string,
  limit = 5,
): Promise<PlayerDevelopmentAnalysisSummary[]> {
  const memories = await searchMemories({
    coach_id: coachId,
    scope: 'player',
    entity_id: playerId,
    tags: ['player-analysis'],
    limit,
  });
  return memories.map((m) => ({
    id: m.id,
    fact: m.fact,
    created_at: m.created_at,
  }));
}
